import { BaseSensorWebView } from '../../base/view/BaseSensorWebView';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { SensorType } from '../../types/sensor-type';
import _ from 'lodash';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { MaxLengthValidator } from '../../../../ui/gzForm/validators/max-length.validator';
import { EditIntegrationConstants } from '../../../editIntegration/EditIntegrationConstants';
import { RegexValidator } from '../../../../ui/gzForm/validators/regex.validator';
import { UrlValidator } from '../../../../ui/gzForm/validators/url.validator';

export class AwsSensorView extends BaseSensorWebView {
  public formGroup: FormGroup;

  public dom: {
    $accessKeyId: GzFormFieldWebComponent;
    $secretAccessKey: GzFormFieldWebComponent;
    $queueUrl: GzFormFieldWebComponent;
  };

  static getRequirements(dependencies: any): string {
    return `
        <span>
            <span style="float: left; font-size: 20px" class="ds-icon ds-icon-notification_info accent mr-2">
            </span>
            <p style = "margin-bottom: 50px; size: 12px; font-weight: 500; display: flow-root">
            ${dependencies.translateFn('IMPORTANT:')} <br>
            ${dependencies.translateFn(
              'Enabling each of the following services may incur additional costs: {AWSCloudTrail}, {AWSConfig}, {amazonSQS} and {amazonSNS}. All these services are required for a successful integration.',
              {
                AWSCloudTrail:
                  '<gz-link type="underline" href="https://aws.amazon.com/cloudtrail/pricing/"><span>' +
                  dependencies.translateFn('AWS Cloud Trail') +
                  '</span></gz-link>',
                AWSConfig:
                  '<gz-link type="underline" href="https://aws.amazon.com/config/pricing/"><span>' +
                  dependencies.translateFn('AWS Config') +
                  '</span></gz-link>',
                amazonSQS:
                  '<gz-link type="underline" href="https://aws.amazon.com/sqs/pricing/"><span>' +
                  dependencies.translateFn('Amazon SQS') +
                  '</span></gz-link>',
                amazonSNS:
                  '<gz-link type="underline" href="https://aws.amazon.com/sns/pricing/"><span>' +
                  dependencies.translateFn('Amazon SNS') +
                  '</span></gz-link>',
              }
            )}
            </p>
        </span>

        <div class="sensor-requirement" sensor-type="${SensorType.AWS}">
        <gz-title class="sensor-title">${dependencies.translateFn(
          'Make sure the following requirements are met:'
        )}</gz-title>

        <ul>
             <li>${dependencies.translateFn('An AWS user account must be set up with the proper permissions.')}</li>
             <li>${dependencies.translateFn(
               'The AWS Config and the AWS CloudTrail services must be enabled and configured.'
             )}</li>
          </ul>
         <p>${dependencies.translateFn('For a complete AWS sensor integration guide, refer to {detailLink}.', {
           detailLink:
             '<gz-link type="underline" href="https://www.bitdefender.com/business/support/en/77211-452345-the-aws-sensor.html"><span>' +
             dependencies.translateFn('this KB article') +
             '</span></gz-link>',
         })}</p>
        </div>
        `;
  }
  static getPopupWarningMessage(dependencies: any, warningText: string): string {
    return (
      '<br><br>' +
      '<span>' +
      '<span class="ds-icon ds-icon-notification_warning warning mr-2" style="float: left; margin-right: 5px; font-size: 20px"></span>' +
      '<p style="font-weight: 470">' +
      dependencies.translateFn('Warning:') +
      '</p>' +
      '</span>' +
      warningText +
      dependencies.translateFn('{AWSCloudTrail}, {AWSConfig}, {amazonSQS} and {amazonSNS}.', {
        AWSCloudTrail:
          '<gz-link type="underline" href="https://aws.amazon.com/cloudtrail/pricing/"><span>' +
          dependencies.translateFn('AWS Cloud Trail') +
          '</span></gz-link>',
        AWSConfig:
          '<gz-link type="underline" href="https://aws.amazon.com/config/pricing/"><span>' +
          dependencies.translateFn('AWS Config') +
          '</span></gz-link>',
        amazonSQS:
          '<gz-link type="underline" href="https://aws.amazon.com/sqs/pricing/"><span>' +
          dependencies.translateFn('Amazon SQS') +
          '</span></gz-link>',
        amazonSNS:
          '<gz-link type="underline" href="https://aws.amazon.com/sns/pricing/"><span>' +
          dependencies.translateFn('Amazon SNS') +
          '</span></gz-link>',
      })
    );
  }

  protected createFormGroup() {
    this.dom.$accessKeyId.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.AccessKeyId.MaxLength,
        this.dependencies.translateFn('The Access Key ID must have maximum {maxLength} characters.', {
          maxLength: EditIntegrationConstants.AccessKeyId.MaxLength,
        })
      ),
    ];
    this.dom.$secretAccessKey.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.SecretAccessKey.MaxLength,
        this.dependencies.translateFn('The Secret Access Key must have maximum {maxLength} characters.', {
          maxLength: EditIntegrationConstants.SecretAccessKey.MaxLength,
        })
      ),
    ];

    this.dom.$queueUrl.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      RegexValidator(/^https.*$/, this.dependencies.translateFn("URL must begin with 'https'.")),
      UrlValidator(this.dependencies.translateFn('URL not valid.')),
      RegexValidator(
        /^https:\/\/sqs\..*\..*\/.*\/.*$/,
        this.dependencies.translateFn('Queue must be in format: ') +
          'https://sqs.{REGION_ENDPOINT}.{ENDPOINT}/{ACCOUNT_NUMBER}/{QUEUE_NAME}.'
      ),
    ];

    this.formGroup = new FormGroup({
      access_key_id: this.dom.$accessKeyId,
      secret_access_key: this.dom.$secretAccessKey,
      queue_url: this.dom.$queueUrl,
    });
  }

  getDomElements(): void {
    super.getDomElements();
    this.dom = {
      $accessKeyId: this.shadow.querySelector('#aws_access_key_id'),
      $secretAccessKey: this.shadow.querySelector('#aws_secret_access_key'),
      $queueUrl: this.shadow.querySelector('#aws_queue_url'),
      ...this.baseDom,
    };
    this.getTranslations();
  }

  setTranslatedStrings(): void {
    this.dom.$accessKeyId.label = this.dependencies.translateFn('Access key ID');
    this.dom.$secretAccessKey.label = this.dependencies.translateFn('Secret access key');
    this.dom.$queueUrl.label = this.dependencies.translateFn('SQS link');
    this.setBaseTranslatedStrings(SensorType.AWS);
  }

  protected async setViewData(viewData) {
    super.setViewData(viewData);
    if (_.get(viewData, ['response', '_id'], false)) {
      this.formGroup.value = {
        access_key_id: _.get(viewData, ['response', 'configuration', 'access_key_id'], ''),
        secret_access_key: _.get(viewData, ['response', 'configuration', 'secret_access_key'], ''),
        queue_url: _.get(viewData, ['response', 'configuration', 'queue_url'], ''),
      };
    }
  }

  public setEnabledAllFields(isEnabled: boolean): void {
    this.baseDom.$sensorSelectWrapper.disabled = !isEnabled;
    if (isEnabled) {
      this.formGroup.setChildrenAttribute('disabled', '');
    } else {
      this.formGroup.setChildrenAttribute('disabled', 'disable');
    }
  }
}
